import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { addClubParis } from '@/modules/cart/infrastructure/CartReduxRepository/CartReduxRepository';
import { CardInput } from '@/modules/perseo-billing/components';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks';
import {
  setIsClubParisModalOpen,
  setIsKeyboardOpen
} from '@/modules/perseo-billing/store/slices/billing';
import {
  Button,
  Divider,
  Link
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src/ui-hooks';

import { schema, yupResolver } from './input.billing.schema';
import { ModalClubParis } from './modal-club-paris.component';

export const FormAddClubCode = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery('xl');
  const { isLoading } = useAppSelector((state) => state.cart);
  const { sendClubParisGtm, clubParisErrorGtm } = useGtmBillingProduct();

  const clubParisForm = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      code: ''
    },
    mode: 'onTouched'
  });

  const {
    formState: { errors, isDirty, isValid },
    watch,
    handleSubmit,
    setFocus,
    reset,
    setValue
  } = clubParisForm;

  const codeValue = watch('code');

  const formatCode = (value: string) => {
    return value
      .replace(/\s+/g, '')
      .replace(/([a-zA-Z0-9]{2})/g, '$1 ')
      .trim();
  };

  const handleSubmitForm = async ({
    code
  }: Record<string, string>): Promise<void> => {
    if (!code) return;

    const codeData = { clubParisCode: code.replace(/\s+/g, '') };
    sendClubParisGtm(codeData.clubParisCode);
    dispatch(addClubParis(codeData));
  };

  useEffect(() => {
    setFocus('code');
    return () => {
      reset();
    };
  }, [reset, setFocus]);

  useEffect(() => {
    if (errors?.code?.message) {
      clubParisErrorGtm(errors.code.message);
    }
  }, [errors?.code?.message, clubParisErrorGtm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatCode(e.target.value);
    setValue('code', formattedValue, { shouldValidate: true });
  };

  const InputIcon = () => {
    if (!codeValue) {
      return null;
    }
    if (!isValid) {
      return <Button icon="InputWarningIcon" />;
    }

    return <Button icon="CheckBillingIcon" />;
  };

  return (
    <FormProvider {...clubParisForm}>
      <form
        className="co-flex co-flex-col co-gap-16px co-pb-16px co-px-24px"
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <Divider />
        <div className="co-w-full co-flex co-flex-col co-gap-2px lg:co-w-1/2">
          <Link
            className="co-self-end lg:co-hidden"
            onClick={() => {
              dispatch(setIsClubParisModalOpen(true));
            }}
          >
            ¿Qué es Club Paris?
          </Link>
          <CardInput
            name="code"
            label={'Ingresa el código'}
            example={!isDirty ? 'Ejemplo: 12 34 56 78' : ''}
            maxLength={14}
            error={errors?.code?.message}
            endIcon={<InputIcon />}
            labelSize="body2"
            isValid={isValid}
            onFocus={() => {
              dispatch(setIsKeyboardOpen(true));
            }}
            value={codeValue || ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="co-w-full lg:co-w-1/2">
          <Button
            pl={32}
            pr={32}
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            disabled={!codeValue}
            loading={isLoading}
          >
            Aplicar
          </Button>
        </div>
        {isMobile && <ModalClubParis />}
      </form>
    </FormProvider>
  );
};
